import { dynamicTranslations } from "@shared/utils/dynamicTranslation";
import React from "react";
import { View } from "react-native";
import { FlexibleTranslationKey } from "../../i18n/types";
import Button from "../md-design-system/Button/Button";
import { ICustomButton } from "./types";

interface WizardNavigationProps {
  isLastStep: boolean;
  isFirstStep?: boolean;
  layout?: "horizontal" | "vertical";
  customButtons?: {
    backButton?: ICustomButton
    nextButton?: ICustomButton;
    finishButton?: ICustomButton;
    customAction?: ICustomButton;
  }
  backText?: FlexibleTranslationKey;
  nextText?: FlexibleTranslationKey;
  finishText?: FlexibleTranslationKey;
  onNext: () => void;
  onPrev: () => void;
  onFinish: () => void;
  isLoading: boolean;
  showBackButton?: boolean;
  showNextButton?: boolean;
  disableBackButton?: boolean;
  disableNextButton?: boolean;
  disableFinishButton?: boolean;
}

const WizardNavigation: React.FC<WizardNavigationProps> = ({
  isLastStep,
  isFirstStep = false,
  backText = "buttons.back",
  nextText = "buttons.continue",
  finishText = "buttons.finalize",
  onNext,
  onPrev,
  onFinish,
  layout = "horizontal",
  customButtons,
  showBackButton = true,
  showNextButton = true,
  disableBackButton = false,
  disableNextButton = false,
  disableFinishButton = false,
}) => {
  const { t } = dynamicTranslations();

  const isCustomFinishButton = customButtons?.finishButton;
  const isCustomNextButton = customButtons?.nextButton;
  const isCustomBackButton = customButtons?.backButton;
  const isCustomAction = customButtons?.customAction;

  // If neither back nor next button should be shown, don't render navigation
  if (!showBackButton && !showNextButton && !isLastStep) {
    return null;
  }

  return (
    <View className="flex-col gap-4 mt-4">
      <View className={`${layout === "horizontal" ? "flex-row justify-between gap-4" : "flex-col-reverse justify-between gap-4"}`}>
        {showBackButton && (
          <View className={`${layout === "horizontal" ? "w-[48%]" : "flex items-center"}`}>
            <Button
              size={isCustomBackButton ? customButtons.backButton?.size : "large"}
              icon={isCustomBackButton ? customButtons.backButton?.icon : "arrow-left"}
              iconPosition={isCustomBackButton ? customButtons.backButton?.iconPosition : "left"}
              customIcon={isCustomBackButton ? customButtons.backButton?.customIcon : undefined}
              variant={isCustomBackButton ? customButtons.backButton?.variant : "outline"}
              onPress={isCustomBackButton ? customButtons.backButton?.onPress : onPrev}
              disabled={isCustomBackButton ? customButtons.backButton?.disabled : (disableBackButton || isFirstStep)}
            >
              {isCustomBackButton ? t(customButtons.backButton?.text || "") : t(backText)}
            </Button>
          </View>
        )}
        {showNextButton && (
          <View className={`${layout === "horizontal" ? !showBackButton ? "w-full" : "w-[48%]" : "w-full"}`}>
            {!isLastStep ? (
              <Button
                size={isCustomNextButton ? customButtons.nextButton?.size : "large"}
                icon={isCustomNextButton ? customButtons.nextButton?.icon : "arrow-right"}
                iconPosition={isCustomNextButton ? customButtons.nextButton?.iconPosition : "right"}
                customIcon={isCustomNextButton ? customButtons.nextButton?.customIcon : undefined}
                variant={isCustomNextButton ? customButtons.nextButton?.variant : "primary"}
                onPress={isCustomNextButton ? customButtons.nextButton?.onPress : onNext}
                disabled={isCustomNextButton ? customButtons.nextButton?.disabled : disableNextButton}
              >
                {isCustomNextButton ? t(customButtons.nextButton?.text || "") : t(nextText)}
              </Button>
            ) : (
              <Button
                size={isCustomFinishButton ? customButtons.finishButton?.size : "large"}
                onPress={isCustomFinishButton ? customButtons.finishButton?.onPress : onFinish}
                variant={isCustomFinishButton ? customButtons.finishButton?.variant : "primary"}
                icon={isCustomFinishButton ? customButtons.finishButton?.icon : undefined}
                iconPosition={isCustomFinishButton ? customButtons.finishButton?.iconPosition : undefined}
                customIcon={isCustomFinishButton ? customButtons.finishButton?.customIcon : undefined}
                disabled={isCustomFinishButton ? customButtons.finishButton?.disabled : disableFinishButton}
              >
                {isCustomFinishButton ? t(customButtons.finishButton?.text || "") : t(finishText)}
              </Button>
            )}
          </View>
        )}
      </View>
      {isCustomAction && (
        <View className="w-full items-center justify-center">
          <View className={`${layout === "horizontal" ? "w-[48%]" : "flex items-center"}`}>
            <Button
              icon={isCustomAction.icon}
              iconPosition={isCustomAction.iconPosition}
              customIcon={isCustomAction.customIcon}
              variant={isCustomAction.variant || "link"}
              onPress={isCustomAction.onPress}
            >
              {t(isCustomAction.text)}
            </Button>
          </View>
        </View>
      )}
    </View>
  );
};

export default WizardNavigation;
